<template>
  <article ref="articleRef">
    <h1 class="mt-8 mb-6" v-html="post.title.rendered" />

    <div class="details flex gap-5 mb-6">
      <span>
        {{ $tt('blog.written_by') }}:
        <AppLink :to="buildAuthorPath(post.author)">
          <strong>{{ getAuthorName(post) }}</strong>
        </AppLink>
      </span>
      <span>
        {{ $tt('blog.published_on') }}
        <strong class="text-primary">{{ date }}</strong>
      </span>
      <span v-if="updatedDate != date">
        {{ $tt('blog.updated_on') }}
        <strong class="text-primary">{{ updatedDate }}</strong>
      </span>
    </div>

    <TableOfContents :headings />

    <AppImg
      :src="getFeaturedImageSrc(post, 'full')"
      :alt="post.title.rendered"
      class="featured my-7"
    />
    <div v-if="post.content" v-html="post.content.rendered" />
  </article>
</template>

<script setup lang="ts">
import { getAuthorName, getFeaturedImageSrc } from '~/helpers/blog';
import { buildAuthorPath } from '~/helpers/routing';
import type { BlogPost } from '~/types';
import { textToIdAttribute } from '~/helpers/format';

const props = defineProps<{
  post: BlogPost;
}>();

const { formatDate } = useFormat();
const date = formatDate(props.post.date);
const updatedDate = formatDate(props.post.modified);

const articleRef = ref<HTMLElement>();
const headings = ref<HTMLElement[]>([]);

onMounted(() => {
  styleImages();
  setHeadings();
});

function styleImages() {
  articleRef.value!.querySelectorAll<HTMLImageElement>('.wp-block-image img').forEach(img => {
    img.removeAttribute('height');
    img.removeAttribute('width');
    img.sizes = `sizes: '(max-width: 1024px) 100vw, 1024px'`;
    img.classList.add('mb-5');
    img.style.borderRadius = '15px';
  });
}
function setHeadings() {
  const headingEls = articleRef.value!.querySelectorAll<HTMLElement>('h2, h3');
  headings.value = Array.from(headingEls);
  headings.value.forEach(setId);
}
function setId(heading: HTMLElement) {
  heading.id = textToIdAttribute(heading.innerText);
}
</script>

<style scoped lang="scss">
article {
  width: 980px;
}
h1 {
  font-size: 32px;
}
.details {
  font-size: 18px;
}
img.featured {
  border-radius: var(--border-r-lg);
}
:deep(ol), :deep(ul), :deep(p) {
  margin-bottom: var(--space-4);
}
:deep(img:only-child), img.featured {
  display: block;
  margin: 0 auto;
}
:deep(img) {
  max-width: 100%;
}
:deep(video) {
  max-width: 100%;
}
:deep(h2) {
  margin-top: var(--space-8);
  margin-bottom: var(--space-5);
  font-size: 30px;
}
:deep(h3) {
  margin-top: var(--space-7);
  margin-bottom: var(--space-4);
  font-size: 28px;
}
:deep(h4), :deep(h5) {
  margin-top: var(--space-6);
}
:deep(p), :deep(li), :deep(h5) {
  font-size: 20px;
}
:deep(.wp-block-embed) {
  width: 100%;
  margin-top: var(--space-6);
  .wp-block-embed__wrapper {
    aspect-ratio: 16 / 9;
    iframe {
      width: 100%;
      height: 100%;
    }
  }
}
@media (max-width: 850px) {
  h1 {
    margin-top: var(--space-7) !important;
    margin-bottom: var(--space-4) !important;
    font-size: 24px;
  }
  .details {
    flex-direction: column;
    gap: var(--space-2);
    font-size: 14px;
  }
  :deep(h2) {
    font-size: 20px;
  }
  :deep(h3) {
    font-size: 18px;
  }
  :deep(p), :deep(li), :deep(h5) {
    font-size: 14px;
  }
}
</style>